// A short code an operator reads out or pastes elsewhere — a Therapist Code
// first. The code itself is `select-all`, so one click selects it for a manual
// copy; the button copies it outright and turns into a check for a moment.

import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import { CheckIcon, CopyIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const COPIED_MS = 1500;

export function CopyableCode({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), COPIED_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copied`, { description: value });
    } catch {
      // Blocked clipboard (an insecure origin, a denied permission): the code
      // is still selectable, so say how to get it instead of failing silently.
      toast.error("Couldn't copy", { description: "Select the code and copy it by hand." });
    }
  }

  return (
    <span className="inline-flex items-center gap-1">
      <Badge className="select-all" mono shape="soft" variant="outline">
        {value}
      </Badge>
      <Button
        aria-label={copied ? `${label} copied` : `Copy ${label} ${value}`}
        onClick={copy}
        shape="soft"
        size="icon-xs"
        title={copied ? "Copied" : `Copy ${label}`}
        type="button"
        variant="ghost"
      >
        {copied ? <CheckIcon className="text-success" weight="bold" /> : <CopyIcon />}
      </Button>
    </span>
  );
}
